import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import {
  SUBMIT_FORM,
  GET_FORM,
  Form,
  FormField,
  FieldType,
  FormSection,
} from "../../graphql/operations";
import { Helmet } from "react-helmet-async";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CreateFormSubmission: React.FC = () => {
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submissionStatus, setSubmissionStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  // Fetch form structure
  const {
    data: formDataResponse,
    loading: formLoading,
    error: formError,
  } = useQuery(GET_FORM, {
    variables: { id: formId },
    skip: !formId,
  });

  const [submitForm, { loading: submitting, error: submitError }] =
    useMutation(SUBMIT_FORM);

  const form: Form | null = formDataResponse?.getForm || null;

  useEffect(() => {
    if (form) {
      // Initialize form data with empty values
      const initialData: Record<string, any> = {};

      // Get all fields from all sections
      const allFields: FormField[] = [];
      form.sections?.forEach((section: FormSection) => {
        allFields.push(...(section.fields || []));
      });

      allFields.forEach((field: FormField) => {
        if (field.type === FieldType.BOOLEAN) {
          initialData[field.label] = false;
        } else if (
          field.type === FieldType.SELECT ||
          field.type === FieldType.MULTISELECT
        ) {
          initialData[field.label] = "";
        } else {
          initialData[field.label] = "";
        }
      });
      setFormData(initialData);
    }
  }, [form]);

  const handleFieldChange = (fieldLabel: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldLabel]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formId || !form) return;

    setSubmissionStatus("submitting");

    try {
      // Prepare submission data in the required format
      const submissionInput = Object.entries(formData).map(
        ([label, value]) => ({
          label,
          value: value.toString(),
        })
      );

      await submitForm({
        variables: {
          formId,
          formTitle: form.title,
          formType: form.formType,
          version: form.version,
          input: submissionInput,
        },
      });

      setSubmissionStatus("success");
      setTimeout(() => {
        navigate("/dashboard/submissions");
      }, 2000);
    } catch (err) {
      setSubmissionStatus("error");
      console.error("Error submitting form:", err);
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.label] || "";

    switch (field.type) {
      case FieldType.TEXT:
      case FieldType.EMAIL:
        return (
          <TextField
            key={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            type={field.type === FieldType.EMAIL ? "email" : "text"}
            sx={{ mb: 2 }}
          />
        );

      case FieldType.NUMBER:
        return (
          <TextField
            key={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            type="number"
            sx={{ mb: 2 }}
          />
        );

      case FieldType.SELECT:
        return (
          <FormControl key={field.label} fullWidth sx={{ mb: 2 }}>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(field.label, e.target.value)}
              required={field.required}
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case FieldType.MULTISELECT:
        return (
          <FormControl key={field.label} fullWidth sx={{ mb: 2 }}>
            <Select
              multiple
              value={value ? value.split(",") : []}
              onChange={(e) =>
                handleFieldChange(field.label, e.target.value.join(","))
              }
              required={field.required}
              renderValue={(selected) => (selected as string[]).join(", ")}
              displayEmpty
            >
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case FieldType.BOOLEAN:
        return (
          <FormControlLabel
            key={field.label}
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) =>
                  handleFieldChange(field.label, e.target.checked)
                }
                required={field.required}
              />
            }
            label={field.label}
            sx={{ mb: 2 }}
          />
        );

      case FieldType.DATE:
        return (
          <TextField
            key={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            type="date"
            placeholder="Select date"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
        );

      default:
        return (
          <TextField
            key={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            required={field.required}
            fullWidth
            sx={{ mb: 2 }}
          />
        );
    }
  };

  if (formLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (formError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading form: {formError.message}
      </Alert>
    );
  }

  if (!form) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Form not found.
      </Alert>
    );
  }

  // Get total fields count from all sections
  const totalFields =
    form.sections?.reduce(
      (total, section) => total + (section.fields?.length || 0),
      0
    ) || 0;

  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto", p: 3 }}>
      <Helmet>
        <title>Almond Tree Dashboard - Submit {form.title}</title>
      </Helmet>

      <Button
        variant="outlined"
        onClick={() => navigate("/dashboard/submissions")}
        sx={{ mb: 3 }}
      >
        ← Back to Submissions
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {form.title}
        </Typography>

        {form.description && (
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {form.description}
          </Typography>
        )}

        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          sx={{ mb: 2 }}
        >
          Form Version: {form.version} • {totalFields} fields across{" "}
          {form.sections?.length || 0} sections
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {form.sections?.map(
              (section: FormSection, sectionIndex: number) => (
                <Accordion key={section.id || sectionIndex} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      {section.title}
                      {section.subtitle && (
                        <Typography variant="body2" color="textSecondary">
                          {section.subtitle}
                        </Typography>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {section.fields?.map((field: FormField) => (
                        <Card key={field.label} variant="outlined">
                          <CardContent>
                            <Typography
                              sx={{ fontSize: 14 }}
                              color="text.secondary"
                              gutterBottom
                            >
                              {field.label}
                            </Typography>
                            {renderField(field)}
                            {field.required && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                * Required field
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )
            )}

            {(submitError || submissionStatus === "error") && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error submitting form:{" "}
                {submitError?.message ||
                  "Please check your inputs and try again."}
              </Alert>
            )}

            {submissionStatus === "success" && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Form submitted successfully! Redirecting to submissions list...
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || submissionStatus === "success"}
              sx={{ mt: 3 }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : (
                "Submit Form"
              )}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateFormSubmission;
